import Button, {ButtonVariant} from 'bitmovin-web-ui/src/components/core/button';
import baseVariables from 'bitmovin-web-ui/src/styles/baseVariables';
import React from 'react';
import {connect} from 'react-redux';
import styled from 'styled-components';

import AccordionPanel from '../../components/accordionPanel';
import {Link, pxToEm} from '../../components/globalStyles';
import WrapperAppUl from '../../components/wrapperAppUl';
import HardcodedSalesDemoPath from '../../data/hardcodedSalesDemoPath';
import {AppRoutePathFunction} from '../../logic/routes';
import {RootState} from '../../logic/store';
import {LoadingState, State as SalesDemosState} from '../../logic/store/salesDemosStore';
import ApiVectorGraphic2Svg from '../../media/cloudEncodingPageView/api_vector_graphic_2.svg';

//#region Styled Components

const StyledWrapperDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
`;

const StyledAccordionPanel = styled(AccordionPanel)`
  width: 100%;

  :not(:last-child) {
    margin-bottom: ${pxToEm(18)}em;
  }
`;

const StyledButton = styled(Button)`
  margin-top: 1em;
`;

const StyledApiVectorGraphicImgWrapper = styled.div`
  text-align: center;
  margin-top: 1em;
  > img {
    height: ${pxToEm(300)}em;
  }
`;

const StyledUlHeadlineSpan = styled.span`
  display: block;
  font-size: ${pxToEm(30)}em;
  color: ${baseVariables.colors.midnight};
  margin: 0.5em 0;
`;

//#endregion

enum AccordionHeadline {
  Drm = 'DRM',
  StreamingProtocols = 'Streaming Protocols',
  SupportedStorage = 'Supported Storage',
  SupportedConnect = 'Cloud Connect',
  SupportedManaged = 'Supported Bitmovin Managed Cloud Environments\n',
  InputVideoCodecsFormats = 'Input Video Codecs',
  InputAudioCodecsFormats = 'Input Audio Codecs',
  LiveInputs = 'Live Inputs',
  OutputAudioCodecs = 'Output Audio Codecs',
  OutputVideoCodecs = 'Output Video Codecs',
  OutputFileFormat = 'Output File Format',
  Hdr = 'HDR (BETA)',
  Filters = 'Filters',
  ClosedCaptionsSubtitles = 'Closed Captions & Subtitles'
}

interface MapStateToProps {
  salesDemos?: SalesDemosState;
}

interface Props extends MapStateToProps {
  className?: string;
}

interface State {
  expandedAccordionHeadlines: AccordionHeadline;
}

class CloudLiveEncodingAccordions extends React.Component<Props, State> {
  public state: Readonly<State> = {
    expandedAccordionHeadlines: undefined
  };

  public componentDidMount(): void {
    this.setState({expandedAccordionHeadlines: undefined});
  }

  private handleAccordionPanelExpand = (accordionHeadline: AccordionHeadline) => {
    const isAlreadyExpanded = this.state.expandedAccordionHeadlines === accordionHeadline;
    this.setState({
      expandedAccordionHeadlines: isAlreadyExpanded ? undefined : accordionHeadline
    });
  };

  public render() {
    return (
      <StyledWrapperDiv className={this.props.className}>
        <StyledAccordionPanel
          headline={AccordionHeadline.LiveInputs}
          expanded={this.state.expandedAccordionHeadlines === AccordionHeadline.LiveInputs}
          onExpand={() => this.handleAccordionPanelExpand(AccordionHeadline.LiveInputs)}>
          <WrapperAppUl>
            <li>RTMP</li>
            <li>SRT</li>
            <li>Zixi</li>
          </WrapperAppUl>
        </StyledAccordionPanel>

        <StyledAccordionPanel
          headline={AccordionHeadline.InputVideoCodecsFormats}
          expanded={this.state.expandedAccordionHeadlines === AccordionHeadline.InputVideoCodecsFormats}
          onExpand={() => this.handleAccordionPanelExpand(AccordionHeadline.InputVideoCodecsFormats)}>
          <WrapperAppUl>
            <li>MPEG-2 Video</li>
            <li>MPEG-4 Video</li>
            <li>H.264/AVC</li>
            <li>H.265/HEVC</li>
          </WrapperAppUl>
        </StyledAccordionPanel>

        <StyledAccordionPanel
          headline={AccordionHeadline.InputAudioCodecsFormats}
          expanded={this.state.expandedAccordionHeadlines === AccordionHeadline.InputAudioCodecsFormats}
          onExpand={() => this.handleAccordionPanelExpand(AccordionHeadline.InputAudioCodecsFormats)}>
          <WrapperAppUl>
            <li>AAC</li>
            <li>AC3 / Dolby Digital</li>
            <li>E-AC3 / Dolby Digital Plus</li>
            <li>PCM</li>
          </WrapperAppUl>
        </StyledAccordionPanel>

        <StyledAccordionPanel
          headline={AccordionHeadline.Drm}
          expanded={this.state.expandedAccordionHeadlines === AccordionHeadline.Drm}
          onExpand={() => this.handleAccordionPanelExpand(AccordionHeadline.Drm)}>
          <WrapperAppUl>
            <li>Hollywood-/Netflix-grade DRM</li>
            <li>Widevine, PlayReady and Marlin with MPEG-CENC</li>
            <li>FairPlay DRM with HLS</li>
          </WrapperAppUl>
          {this.renderDemoLinkButton(HardcodedSalesDemoPath.Drm)}
        </StyledAccordionPanel>

        <StyledAccordionPanel
          headline={AccordionHeadline.ClosedCaptionsSubtitles}
          expanded={this.state.expandedAccordionHeadlines === AccordionHeadline.ClosedCaptionsSubtitles}
          onExpand={() => this.handleAccordionPanelExpand(AccordionHeadline.ClosedCaptionsSubtitles)}>
          <WrapperAppUl>
            <li>WebVTT</li>
            <li>CEA-608/708</li>
            <li>OCR into WebVTT</li>
          </WrapperAppUl>
        </StyledAccordionPanel>

        <StyledAccordionPanel
          headline={AccordionHeadline.Hdr}
          expanded={this.state.expandedAccordionHeadlines === AccordionHeadline.Hdr}
          onExpand={() => this.handleAccordionPanelExpand(AccordionHeadline.Hdr)}>
          <WrapperAppUl>
            <li>HLG</li>
            <li>HDR10</li>
          </WrapperAppUl>
        </StyledAccordionPanel>

        <StyledAccordionPanel
          headline={AccordionHeadline.Filters}
          expanded={this.state.expandedAccordionHeadlines === AccordionHeadline.Filters}
          onExpand={() => this.handleAccordionPanelExpand(AccordionHeadline.Filters)}>
          <WrapperAppUl>
            <li>Motion Adaptive Deinterlace (Standard)</li>
          </WrapperAppUl>
        </StyledAccordionPanel>

        <StyledAccordionPanel
          headline={AccordionHeadline.StreamingProtocols}
          expanded={this.state.expandedAccordionHeadlines === AccordionHeadline.StreamingProtocols}
          onExpand={() => this.handleAccordionPanelExpand(AccordionHeadline.StreamingProtocols)}>
          <WrapperAppUl>
            <li>MPEG-DASH</li>
            <li>Apple HLS</li>
          </WrapperAppUl>
        </StyledAccordionPanel>

        <StyledAccordionPanel
          headline={AccordionHeadline.OutputVideoCodecs}
          expanded={this.state.expandedAccordionHeadlines === AccordionHeadline.OutputVideoCodecs}
          onExpand={() => this.handleAccordionPanelExpand(AccordionHeadline.OutputVideoCodecs)}>
          <WrapperAppUl>
            <li>H.264/AVC</li>
            <li>H.265/HEVC</li>
            <li>VP9</li>
          </WrapperAppUl>
        </StyledAccordionPanel>

        <StyledAccordionPanel
          headline={AccordionHeadline.OutputAudioCodecs}
          expanded={this.state.expandedAccordionHeadlines === AccordionHeadline.OutputAudioCodecs}
          onExpand={() => this.handleAccordionPanelExpand(AccordionHeadline.OutputAudioCodecs)}>
          <WrapperAppUl>
            <li>AAC-LC</li>
            <li>HE-AACv1</li>
            <li>HE-AACv2</li>
            <li>Dolby Digital</li>
            <li>Dolby Digital Plus </li>
          </WrapperAppUl>
        </StyledAccordionPanel>

        <StyledAccordionPanel
          headline={AccordionHeadline.OutputFileFormat}
          expanded={this.state.expandedAccordionHeadlines === AccordionHeadline.OutputFileFormat}
          onExpand={() => this.handleAccordionPanelExpand(AccordionHeadline.OutputFileFormat)}>
          <WrapperAppUl>
            <li>MPEG-2 TS</li>
            <li>fMP4</li>
            <li>WebM</li>
            <li>CMAF</li>
          </WrapperAppUl>
        </StyledAccordionPanel>

        <StyledAccordionPanel
          headline={AccordionHeadline.SupportedStorage}
          expanded={this.state.expandedAccordionHeadlines === AccordionHeadline.SupportedStorage}
          onExpand={() => this.handleAccordionPanelExpand(AccordionHeadline.SupportedStorage)}>
          <WrapperAppUl>
            <li>AWS S3</li>
            <li>Google Cloud Storage</li>
            <li>Azure Blob</li>
            <li>HTTP/HTTPs</li>
            <li>Akamai NetStorage</li>
          </WrapperAppUl>
        </StyledAccordionPanel>

        <StyledAccordionPanel
          headline={AccordionHeadline.SupportedManaged}
          expanded={this.state.expandedAccordionHeadlines === AccordionHeadline.SupportedManaged}
          onExpand={() => this.handleAccordionPanelExpand(AccordionHeadline.SupportedManaged)}>
          <WrapperAppUl>
            <li>Amazon Web Services</li>
            <li>Google Cloud Platform </li>
            <li>Microsoft Azure</li>
          </WrapperAppUl>
        </StyledAccordionPanel>

        <StyledAccordionPanel
          headline={AccordionHeadline.SupportedConnect}
          expanded={this.state.expandedAccordionHeadlines === AccordionHeadline.SupportedConnect}
          onExpand={() => this.handleAccordionPanelExpand(AccordionHeadline.SupportedConnect)}>
          <WrapperAppUl>
            <li>Amazon Web Services</li>
            <li>Google Cloud Platform </li>
            <li>Microsoft Azure</li>
          </WrapperAppUl>
        </StyledAccordionPanel>
      </StyledWrapperDiv>
    );
  }

  public renderDemoLinkButton = (demoPath: string) => {
    const {loadingState, response} = this.props.salesDemos;

    if (loadingState !== LoadingState.Success) {
      return null;
    }

    const allLoadedDemos = response.demos || [];
    const foundDemo = allLoadedDemos.find(salesDemo => salesDemo.path === demoPath);
    if (foundDemo === undefined) {
      return null;
    }

    return (
      <Link to={AppRoutePathFunction.DemoDetail({demoId: foundDemo.id})}>
        <StyledButton variant={ButtonVariant.OutlinedLight}>{foundDemo.name}&nbsp;Demo</StyledButton>
      </Link>
    );
  };
}

const mapStateToProps = (state: RootState): MapStateToProps => ({
  salesDemos: state.salesDemos
});

export default connect(mapStateToProps)(CloudLiveEncodingAccordions);
